import React, { useEffect, useRef, useState } from 'react';
import { useAppStore } from '../store';
import { Avatar } from './ui/Avatar';
import { Button } from './ui/Buttons';
import { AvatarEditor } from './AvatarEditor';
import { uploadAvatar, convertToBase64 } from '../lib/firebase/storage';
import { 
  Settings, 
  Upload, 
  Edit3, 
  Palette, 
  Check,
  X
} from 'lucide-react';
import { cn } from '../lib/utils';
import { useNavigate } from 'react-router-dom';

const AVATAR_COLOR_SCHEMES = [
  { id: 'blue', bg: 'bg-blue-100 dark:bg-blue-900', text: 'text-blue-700 dark:text-blue-200', border: 'border-blue-300 dark:border-blue-700', name: 'Ocean Blue' },
  { id: 'purple', bg: 'bg-purple-100 dark:bg-purple-900', text: 'text-purple-700 dark:text-purple-200', border: 'border-purple-300 dark:border-purple-700', name: 'Royal Purple' },
  { id: 'emerald', bg: 'bg-emerald-100 dark:bg-emerald-900', text: 'text-emerald-700 dark:text-emerald-200', border: 'border-emerald-300 dark:border-emerald-700', name: 'Forest Green' },
  { id: 'amber', bg: 'bg-amber-100 dark:bg-amber-900', text: 'text-amber-700 dark:text-amber-200', border: 'border-amber-300 dark:border-amber-700', name: 'Sunset Orange' },
  { id: 'rose', bg: 'bg-rose-100 dark:bg-rose-900', text: 'text-rose-700 dark:text-rose-200', border: 'border-rose-300 dark:border-rose-700', name: 'Blossom Pink' },
];

interface ProfileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement>;
}

export const ProfileMenu: React.FC<ProfileMenuProps> = ({ isOpen, onClose, anchorRef }) => {
  const { user, updateUser, addNotification, mode } = useAppStore();
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);
  const [showAvatarEditor, setShowAvatarEditor] = useState(false);
  const [avatarEditorMode, setAvatarEditorMode] = useState<'edit' | 'upload'>('edit');

  const isUsingDefaultAvatar = !user?.avatar || user.avatar.trim() === '';
  const currentColorScheme = user?.avatarColorScheme || 'blue';

  // Close menu when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        anchorRef.current &&
        !anchorRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose, anchorRef]);

  // Position menu above the avatar
  useEffect(() => {
    if (!isOpen || !menuRef.current || !anchorRef.current) return;

    const anchorRect = anchorRef.current.getBoundingClientRect();
    const menuRect = menuRef.current.getBoundingClientRect();
    
    // Position to the left of the sidebar, aligned with bottom of avatar
    const left = Math.max(12, anchorRect.left - menuRect.width - 12);
    const top = Math.max(12, anchorRect.bottom - menuRect.height);
    
    menuRef.current.style.left = `${left}px`;
    menuRef.current.style.top = `${top}px`;
    
    console.log('[ProfileMenu] Positioned at:', { left, top, menuRect, anchorRect });
  }, [isOpen, anchorRef]);

  const handleColorSchemeSelect = async (schemeId: string) => {
    if (!user) return;
    
    try {
      await updateUser({ avatarColorScheme: schemeId });
      addNotification('Avatar color updated', 'success');
      onClose();
    } catch (error: any) {
      addNotification(error.message || 'Failed to update color', 'error');
    }
  };

  const handleEditPhoto = () => {
    setAvatarEditorMode('edit');
    setShowAvatarEditor(true);
    onClose();
  };

  const handleUploadNew = () => {
    setAvatarEditorMode('upload');
    setShowAvatarEditor(true);
    onClose();
  };

  const handleAvatarConfirm = async (croppedImageBlob: Blob) => {
    if (!user) return;

    try {
      let avatarUrl: string;
      
      if (mode === 'firebase') {
        // Upload to Firebase Storage
        avatarUrl = await uploadAvatar(user.id, croppedImageBlob);
      } else {
        // Mock mode: convert to base64
        avatarUrl = await convertToBase64(croppedImageBlob);
      }

      await updateUser({ avatar: avatarUrl });
      addNotification('Avatar updated', 'success');
    } catch (error: any) {
      addNotification(error.message || 'Failed to update avatar', 'error');
    }
  };

  const handleSettingsClick = () => {
    navigate('/settings');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        ref={menuRef}
        className="fixed z-[100] w-80 bg-card border border-border rounded-2xl shadow-2xl animate-in fade-in zoom-in-95 duration-200"
        style={{ position: 'fixed' }}
      >
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center gap-4">
            <Avatar
              src={user?.avatar}
              name={user?.name}
              size="lg"
              colorScheme={currentColorScheme}
              className="ring-2 ring-border"
            />
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-foreground truncate">{user?.name}</h3>
              <p className="text-xs text-muted-foreground font-medium">{user?.role}</p>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-muted rounded-lg transition-colors"
            >
              <X size={16} className="text-muted-foreground" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto custom-scrollbar">
          {/* Color Schemes Section - Always visible */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 px-1">
              <Palette size={14} className="text-muted-foreground" />
              <h4 className="text-xs font-black uppercase tracking-widest text-muted-foreground">
                Initials Color Scheme
              </h4>
            </div>
            <div className="grid grid-cols-5 gap-2">
              {AVATAR_COLOR_SCHEMES.map((scheme) => {
                const isSelected = currentColorScheme === scheme.id;
                return (
                  <button
                    key={scheme.id}
                    onClick={() => handleColorSchemeSelect(scheme.id)}
                    className={cn(
                      'relative aspect-square rounded-full flex items-center justify-center transition-all hover:scale-110',
                      scheme.bg,
                      isSelected && 'ring-2 ring-offset-2 ring-primary'
                    )}
                    title={scheme.name}
                  >
                    <div className={cn('w-6 h-6 rounded-full', scheme.bg, scheme.border, 'border-2')} />
                    {isSelected && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Check size={16} className={scheme.text} />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            <p className="text-[10px] text-muted-foreground px-1">
              {isUsingDefaultAvatar 
                ? "Choose a color scheme for your avatar initials"
                : "Choose colors for your initials (applies when photo is removed)"}
            </p>
          </div>

          {/* Photo Options Section */}
          <div className="space-y-2 pt-2 border-t border-border">
            <div className="flex items-center gap-2 px-1">
              <Edit3 size={14} className="text-muted-foreground" />
              <h4 className="text-xs font-black uppercase tracking-widest text-muted-foreground">
                Photo Options
              </h4>
            </div>
            
            {user?.avatar && (
              <Button
                variant="outline"
                className="w-full justify-start gap-2"
                onClick={handleEditPhoto}
              >
                <Edit3 size={16} />
                Edit Current Photo
              </Button>
            )}
            
            <Button
              variant="outline"
              className="w-full justify-start gap-2"
              onClick={handleUploadNew}
            >
              <Upload size={16} />
              {user?.avatar ? 'Replace Photo' : 'Upload Photo'}
            </Button>
          </div>

          {/* Settings Link */}
          <div className="pt-2 border-t border-border">
            <Button
              variant="ghost"
              className="w-full justify-start gap-2 text-muted-foreground hover:text-foreground"
              onClick={handleSettingsClick}
            >
              <Settings size={16} />
              Account Settings
            </Button>
          </div>
        </div>
      </div>

      {/* Avatar Editor Modal */}
      <AvatarEditor
        currentAvatar={avatarEditorMode === 'edit' ? user?.avatar : undefined}
        isOpen={showAvatarEditor}
        onClose={() => setShowAvatarEditor(false)}
        onConfirm={handleAvatarConfirm}
      />
    </>
  );
};
